import { Injectable, EventEmitter, inject } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { langageCultureToLanguage } from 'app/helpers/language.helper';
import { Lang } from 'app/models/lang.model';
import { BehaviorSubject, Observable, Subscription, map } from 'rxjs';
import dayjs from 'dayjs';
import { Store } from '@ngrx/store';
import { selectMeCurrency, selectSettings } from 'app/store/me/me.selectors';
import { CurrencyType } from './gift-cards-plans.service';
import { MeActions } from 'app/store/me/me.actions';
import { Settings } from 'app/modules/dashboard/models/settings.model';
import { DatePipe } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate: TranslateService = inject(TranslateService);
  private store: Store = inject(Store);

  langEmitter: EventEmitter<string> = new EventEmitter<string>();
  langChange: EventEmitter<string> = new EventEmitter<string>();
  currentLanguage: string;
  private readonly currentLanguageSubject$: BehaviorSubject<string> =
    new BehaviorSubject('');
  private currencyInitialized: boolean = false;

  readonly currentLanguage$: Observable<string> =
    this.currentLanguageSubject$.asObservable();

  subscriptions: Subscription;
  currency: BehaviorSubject<CurrencyType> = new BehaviorSubject('eur');
  settings$: Observable<Settings> = this.store.select(selectSettings);
  datePipe: DatePipe = new DatePipe(dayjs.locale());

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  ngOnInit(): void {
    this.subscriptions = this.translate.onLangChange.subscribe(
      this.onLanguageChange.bind(this)
    );
    this.subscriptions.add(
      this.translate.onDefaultLangChange.subscribe(
        this.onLanguageChange.bind(this)
      )
    );
  }

  set(lang: string): void {
    localStorage.setItem('lang', lang);
    this.currentLanguage = langageCultureToLanguage(lang);
    this.updateDayjsLocale(this.currentLanguage);
    this.langEmitter.emit(lang);
    this.setCurrency(lang);
    this.store.dispatch(MeActions.getSettings());
  }

  get(): string {
    let localeLanguage: string = navigator.language;
    if (localeLanguage) {
      if (!localeLanguage.includes('-')) {
        localeLanguage += '-' + localeLanguage.toUpperCase();
      }
      if (
        [
          'en-EN',
          'fr-FR',
          'de-DE',
          'es-ES',
          'it-IT',
          'nl-NL',
          'en-US',
        ].includes(localeLanguage)
      ) {
        if (localStorage.getItem('lang')) {
          this.setCurrency(localStorage.getItem('lang'));
          return localStorage.getItem('lang');
        } else {
          this.set(localeLanguage);
          return localeLanguage;
        }
      } else {
        this.setCurrency(localStorage.getItem('lang'));
        return localStorage.getItem('lang');
      }
    }
  }

  setCurrency(lang: string): void {
    if (!this.currencyInitialized) {
      this.currencyInitialized = true;
      this.store
        .select(selectMeCurrency)
        .subscribe((currency: CurrencyType) => {
          console.log('[LanguageService] devise reçue du store:', currency);
          this.currency.next(currency ?? 'eur');
        });
    }
  }

  format(): string {
    if (this.get()) {
      return this.get().split('-')[0];
    }
  }

  getTranslateLanguage(): string {
    return this.currentLanguage;
  }

  onLanguageChange({ lang }: LangChangeEvent) {
    this.currentLanguage = langageCultureToLanguage(lang);
    this.currentLanguageSubject$.next(this.currentLanguage);
    this.updateDayjsLocale(this.currentLanguage);
    this.langChange.emit(this.currentLanguage);
  }

  updateDayjsLocale(language: string): void {
    const dateLocale: string = Object.keys(Lang).includes(
      language.toUpperCase()
    )
      ? language
      : Lang.EN;
    dayjs.locale(dateLocale);
  }

  getDisplayedShortDate$(date: Date | null): Observable<string | null> {
    return this.settings$.pipe(
      map((settings: Settings) =>
        this.datePipe.transform(
          date,
          settings.default_date_format?.split(' ')[0].replace('Y', 'yy')
        )
      )
    );
  }

  getDisplayedLongDate$(date: Date | null): Observable<string | null> {
    return this.settings$.pipe(
      map((settings: Settings) =>
        this.datePipe.transform(
          date,
          settings.default_date_format?.split(' ')[0]
        )
      )
    );
  }
}
